/**
 * useArchive
 *
 * Thin `archiveArtifact` callable wrappers for every artifact type, consumed by
 * the archive UI and by `useBulkFormActions`. Single-artifact archives resolve to
 * a boolean; `archiveMultipleForms` instead drains its input in batches of
 * `FORM_ARCHIVE_BATCH_SIZE` because every form is its own callable invocation and
 * a wide fan-out OOM-kills a single Cloud Run container. Batches settle
 * independently, so one rejection never aborts the forms behind it, and the
 * returned `succeededIds` are the only ids the caller may treat as archived.
 */

import { useState, useCallback } from 'react';
import { FORM_ARCHIVE_BATCH_SIZE } from '#types/System.Parameters.types';
import { archiveArtifact } from '../../services/artifactFunctions';
import type { BulkUpdateResult } from '../forms/useBulkFormActions';

// Local type definition for artifact types
export type ArtifactType = 'form' | 'formType' | 'entity' | 'entityRecord' | 'formVersion';

/**
 * Hook for archiving and unarchiving documents
 */
export const useArchive = () => {
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  /**
   * Archives a form without confirmation (confirmation should be handled by the component)
   */
  const archiveForm = useCallback(async (formId: string) => {
    setIsArchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'form',
        artifactId: formId,
        action: 'archive'
      });
      return true;
    } catch (error) {
      console.error('Failed to archive form:', error);
      return false;
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Unarchives a form
   */
  const unarchiveForm = useCallback(async (formId: string) => {
    setIsUnarchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'form',
        artifactId: formId,
        action: 'unarchive'
      });
      return true;
    } catch (error) {
      console.error('Failed to unarchive form:', error);
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  }, []);

  /**
   * Archives multiple forms, at most `FORM_ARCHIVE_BATCH_SIZE` at a time.
   */
  const archiveMultipleForms = useCallback(async (formIds: string[]): Promise<BulkUpdateResult> => {
    setIsArchiving(true);
    try {
      const batches = Array.from(
        { length: Math.ceil(formIds.length / FORM_ARCHIVE_BATCH_SIZE) },
        (_v, idx) => formIds.slice(idx * FORM_ARCHIVE_BATCH_SIZE, idx * FORM_ARCHIVE_BATCH_SIZE + FORM_ARCHIVE_BATCH_SIZE)
      );

      const archiveOne = async (formId: string): Promise<string> => {
        await archiveArtifact({
          artifactType: 'form',
          artifactId: formId,
          action: 'archive'
        });
        return formId;
      };

      const succeededIds = await batches.reduce<Promise<string[]>>(
        async (accPromise, batch) => {
          const acc = await accPromise;
          const settled = await Promise.allSettled(batch.map(archiveOne));
          const archived = settled.flatMap((result) => {
            if (result.status === 'fulfilled') return [result.value];
            console.error('Failed to archive form:', result.reason);
            return [];
          });
          return [...acc, ...archived];
        },
        Promise.resolve([])
      );

      return {
        successCount: succeededIds.length,
        failureCount: formIds.length - succeededIds.length,
        succeededIds
      };
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Archives an entity record
   */
  const archiveEntityRecord = useCallback(async (
    entityId: string,
    recordId: string
  ) => {
    setIsArchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'entityRecord',
        artifactId: recordId,
        parentId: entityId,
        action: 'archive'
      });
      return true;
    } catch (error) {
      console.error('Failed to archive record:', error);
      return false;
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Unarchives an entity record
   */
  const unarchiveEntityRecord = useCallback(async (recordId: string, entityId?: string) => {
    setIsUnarchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'entityRecord',
        artifactId: recordId,
        parentId: entityId,
        action: 'unarchive'
      });
      return true;
    } catch (error) {
      console.error('Failed to unarchive record:', error);
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  }, []);

  /**
   * Archives a form type. Rejects with the backend error so the caller can
   * surface its reason (e.g. permission denied).
   */
  const archiveFormType = useCallback(async (formTypeId: string) => {
    setIsArchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'formType',
        artifactId: formTypeId,
        action: 'archive'
      });
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Unarchives a form type
   */
  const unarchiveFormType = useCallback(async (formTypeId: string) => {
    setIsUnarchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'formType',
        artifactId: formTypeId,
        action: 'unarchive'
      });
      return true;
    } catch (error) {
      console.error('Failed to unarchive form type:', error);
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  }, []);

  /**
   * Archives an entity. Rejects with the backend error so the caller can
   * surface its reason (e.g. permission denied).
   */
  const archiveEntity = useCallback(async (entityId: string) => {
    setIsArchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'entity',
        artifactId: entityId,
        action: 'archive'
      });
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Unarchives an entity
   */
  const unarchiveEntity = useCallback(async (entityId: string) => {
    setIsUnarchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'entity',
        artifactId: entityId,
        action: 'unarchive'
      });
      return true;
    } catch (error) {
      console.error('Failed to unarchive record type:', error);
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  }, []);

  /**
   * Archives a form version
   */
  const archiveFormVersion = useCallback(async (formId: string, versionId: string) => {
    setIsArchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'formVersion',
        artifactId: versionId,
        parentId: formId,
        action: 'archive'
      });
      return true;
    } catch (error) {
      console.error('Failed to archive form version:', error);
      return false;
    } finally {
      setIsArchiving(false);
    }
  }, []);

  /**
   * Unarchives a form version
   */
  const unarchiveFormVersion = useCallback(async (versionId: string, formId?: string) => {
    setIsUnarchiving(true);
    try {
      await archiveArtifact({
        artifactType: 'formVersion',
        artifactId: versionId,
        parentId: formId,
        action: 'unarchive'
      });
      return true;
    } catch (error) {
      console.error('Failed to unarchive form version:', error);
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  }, []);

  return {
    archiveForm,
    unarchiveForm,
    archiveMultipleForms,
    archiveEntityRecord,
    unarchiveEntityRecord,
    archiveFormType,
    unarchiveFormType,
    archiveEntity,
    unarchiveEntity,
    archiveFormVersion,
    unarchiveFormVersion,
    isArchiving,
    isUnarchiving
  };
};